/**
 * UnifiedLoginPage - Modern Cathedral Design
 * Tabs: Admin | Member | Delegate
 * Split layout with luxury aesthetic
 * Customizable via Portal Settings
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import { cn } from '@/lib/utils';
import afterLogin from '@/lib/afterLogin';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';

// Icons
import {
  User, Mail, Lock, Eye, EyeOff, ArrowRight, Shield,
  Users, BookOpen, Loader2, ChevronRight, Calendar, Church,
  Heart, CreditCard, Home
} from 'lucide-react';

const UnifiedLoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuthStore();
  
  // Fetch login page config
  const { data: config, isLoading: loadingConfig } = useQuery({
    queryKey: ['login-config'],
    queryFn: () => api.get('/public/login-config').then(r => r.data).catch(() => ({})),
  });
  
  // Determine default tab based on URL params and config
  const getDefaultTab = () => {
    const urlType = searchParams.get('type');
    if (urlType) return urlType;
    if (config?.showMemberTab !== false) return 'member';
    if (config?.showAdminTab !== false) return 'admin';
    if (config?.showDelegateTab !== false) return 'delegate';
    return 'member';
  };
  
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  const [showPassword, setShowPassword] = useState(false);
  
  // Update tab when config loads
  useEffect(() => {
    if (config && !searchParams.get('type')) {
      setActiveTab(getDefaultTab());
    }
  }, [config]);
  
  // Form states
  const [adminForm, setAdminForm] = useState({ email: '', password: '', remember_me: false });
  const [memberForm, setMemberForm] = useState({ email: '', password: '' });
  const [delegateForm, setDelegateForm] = useState({ email: '', password: '', eventCode: '' });

  // Admin Login
  const adminLoginMutation = useMutation({
    mutationFn: (data) => api.post('/admin/login', data),
    onSuccess: async (response) => {
      const { access_token, admin, permissions } = response.data;
      setAuth(access_token, admin || { email: adminForm.email }, 'admin', null, permissions || []);
      toast.success('Welcome back!');
      const redirect = new URLSearchParams(window.location.search).get('redirect') || '';
      await afterLogin({ navigate, userType: 'admin', tenantId: null, redirect });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Invalid credentials'));
    },
  });

  // Member Login — with smart fallback to Admin if member auth fails.
  // Why: many users don't realise "team member of an event" = "admin".
  // They land on /login (which defaults to Member tab), enter their admin
  // credentials and see a confusing 401. Now we silently retry against
  // /admin/login so the right thing just works.
  const memberLoginMutation = useMutation({
    mutationFn: async (data) => {
      try {
        const r = await api.post('/member/login', data);
        return { kind: 'member', response: r };
      } catch (memberErr) {
        const status = memberErr.response?.status;
        if (status === 401 || status === 404) {
          // Fall back to admin login. If THAT also fails, surface a unified error.
          try {
            const r = await api.post('/admin/login', { ...data, remember_me: false });
            return { kind: 'admin', response: r };
          } catch (adminErr) {
            // Re-throw the member error so the user sees the original message.
            throw memberErr;
          }
        }
        throw memberErr;
      }
    },
    onSuccess: async ({ kind, response }) => {
      if (kind === 'admin') {
        const { access_token, admin, permissions } = response.data;
        setAuth(access_token, admin || { email: memberForm.email }, 'admin', null, permissions || []);
        toast.success('Welcome back!');
        const redirect = new URLSearchParams(window.location.search).get('redirect') || '';
        await afterLogin({ navigate, userType: 'admin', tenantId: null, redirect });
        return;
      }
      const { access_token, member, token } = response.data;
      // A member promoted to an office that runs an assembly (Pastor and up)
      // carries the church workspace, so the session is stored as an admin one.
      const runsChurch = !!member?.is_church_admin;
      setAuth(access_token || token, member, runsChurch ? 'admin' : 'member',
              null, member?.permissions || []);
      toast.success(runsChurch && member?.office_label
        ? `Welcome back, ${member.office_label}`
        : 'Welcome back!');
      const redirect = new URLSearchParams(window.location.search).get('redirect') || '';
      await afterLogin({ navigate, userType: runsChurch ? 'admin' : 'member',
                         tenantId: null, redirect, token: access_token || token });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Invalid credentials'));
    },
  });

  // Delegate Login
  const delegateLoginMutation = useMutation({
    mutationFn: (data) => api.post('/delegate/login', data),
    onSuccess: async (response) => {
      const { access_token, delegate_name, delegate_id, event_id, token } = response.data;
      localStorage.setItem('eventflow_token', access_token || token);
      const delegateUser = { id: delegate_id, name: delegate_name, event_id };
      setAuth(access_token || token, delegateUser, 'delegate', event_id);
      toast.success(`Welcome, ${delegate_name || 'Delegate'}!`);
      const redirect = new URLSearchParams(window.location.search).get('redirect') || '';
      await afterLogin({ navigate, userType: 'delegate', tenantId: null, redirect });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Invalid credentials'));
    },
  });

  const handleAdminLogin = (e) => {
    e.preventDefault();
    adminLoginMutation.mutate(adminForm);
  };

  const handleMemberLogin = (e) => {
    e.preventDefault();
    memberLoginMutation.mutate(memberForm);
  };

  const handleDelegateLogin = (e) => {
    e.preventDefault();
    // Send both `email` and `username` — backend accepts either, and this keeps
    // the same input field working for phone-based delegates.
    const identifier = delegateForm.email;
    delegateLoginMutation.mutate({
      email: identifier.includes('@') ? identifier : null,
      username: identifier,
      password: delegateForm.password,
    });
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image with Quote */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src={config?.backgroundImage || "https://images.unsplash.com/photo-1628587469578-42240aedb73a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-900/90" />
        
        {/* Quote */}
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="max-w-md">
            <div className="w-12 h-1 bg-gold mb-6" />
            <blockquote className="font-heading text-3xl leading-relaxed mb-4">
              "{config?.quoteText || "For where two or three gather in my name, there am I with them."}"
            </blockquote>
            <cite className="text-gold font-medium">— {config?.quoteReference || "Matthew 18:20"}</cite>
          </div>
          
          {/* Decorative Elements */}
          <div className="absolute top-12 left-12">
            <BookOpen className="h-10 w-10 text-gold/30" strokeWidth={1} />
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="w-full max-w-md">
          {/* Logo/Brand */}
          <div className="text-center mb-10">
            {(() => {
              const raw = (config?.logoUrl || "").trim();
              const resolved = !raw
                ? ""
                : raw.startsWith("http") ? raw
                : raw.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${raw}`
                : `${process.env.REACT_APP_BACKEND_URL}/${raw}`;
              const [imgFailed, setImgFailed] = [false, () => {}];
              // We deliberately don't useState here because hooks can't be in a callback.
              return resolved ? (                <img
                  src={resolved}
                  alt="Logo"
                  className="h-16 mx-auto mb-4 object-contain"
                  data-testid="login-logo"
                  onError={(e) => {
                    // Hide the broken image and reveal the fallback block.
                    // `nextSibling` can be a whitespace text node, which has no
                    // style and silently swallowed the fallback (iter 702).
                    e.currentTarget.style.display = "none";
                    const fb = e.currentTarget.nextElementSibling;
                    if (fb && fb.style) fb.style.display = "flex";
                  }}
                />
              ) : null;
            })()}
            {/* Fallback (hidden by default when an img is present; shown via onError) */}
            <div
              className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center shadow-lg"
              style={{ display: (config?.logoUrl || "").trim() ? "none" : "flex" }}
              data-testid="login-logo-fallback"
            >
              <BookOpen className="h-8 w-8 text-gold" strokeWidth={1.5} />
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">{config?.welcomeTitle || "Welcome"}</h1>
            <p className="text-slate-500 mt-2 font-body">{config?.subtitle || "Sign in to continue"}</p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className={cn(
              "grid w-full bg-slate-100 p-1 rounded-full mb-8",
              // Dynamically set grid columns based on visible tabs
              [config?.showAdminTab, config?.showMemberTab, config?.showDelegateTab].filter(t => t !== false).length === 3 ? "grid-cols-3" :
              [config?.showAdminTab, config?.showMemberTab, config?.showDelegateTab].filter(t => t !== false).length === 2 ? "grid-cols-2" :
              "grid-cols-1"
            )}>
              {config?.showAdminTab !== false && (
                <TabsTrigger 
                  value="admin" 
                  className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium"
                  data-testid="admin-tab"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Admin
                </TabsTrigger>
              )}
              {config?.showMemberTab !== false && (
                <TabsTrigger 
                  value="member" 
                  className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium"
                  data-testid="member-tab"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Member
                </TabsTrigger>
              )}
              {config?.showDelegateTab !== false && (
                <TabsTrigger 
                  value="delegate" 
                  className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium"
                  data-testid="delegate-tab"
                >
                  <User className="h-4 w-4 mr-2" />
                  Delegate
                </TabsTrigger>
              )}
            </TabsList>

            {/* Admin Login */}
            <TabsContent value="admin">
              <form onSubmit={handleAdminLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="admin-email" className="text-slate-700 font-medium">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="admin-email"
                      type="email"
                      placeholder="admin@example.com"
                      value={adminForm.email}
                      onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                      className="h-12 pl-12 rounded-xl border-slate-200 focus:border-slate-400 focus:ring-slate-400/20"
                      data-testid="admin-email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="admin-password" className="text-slate-700 font-medium">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="admin-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={adminForm.password}
                      onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                      className="h-12 pl-12 pr-12 rounded-xl border-slate-200 focus:border-slate-400 focus:ring-slate-400/20"
                      data-testid="admin-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm text-slate-600 select-none cursor-pointer px-1">
                  <input
                    type="checkbox"
                    checked={adminForm.remember_me}
                    onChange={(e) => setAdminForm({ ...adminForm, remember_me: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300"
                    data-testid="admin-remember-me"
                  />
                  Remember me for 30 days
                </label>

                <Button
                  type="submit"
                  disabled={adminLoginMutation.isPending}
                  className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-medium shadow-lg hover:shadow-xl transition-all"
                  data-testid="admin-login-btn"
                >
                  {adminLoginMutation.isPending ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>Sign In <ArrowRight className="h-4 w-4 ml-2" /></>
                  )}
                </Button>
                
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password?type=admin')}
                    className="text-sm text-slate-500 hover:text-slate-700 hover:underline transition-colors"
                    data-testid="admin-forgot-password-link"
                  >
                    Forgot Password?
                  </button>
                </div>
              </form>
            </TabsContent>

            {/* Member Login */}
            <TabsContent value="member">
              <form onSubmit={handleMemberLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="member-email" className="text-slate-700 font-medium">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="member-email"
                      type="email"
                      placeholder="member@example.com"
                      value={memberForm.email}
                      onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                      className="h-12 pl-12 rounded-xl border-slate-200 focus:border-slate-400 focus:ring-slate-400/20"
                      data-testid="member-email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="member-password" className="text-slate-700 font-medium">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="member-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={memberForm.password}
                      onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })}
                      className="h-12 pl-12 pr-12 rounded-xl border-slate-200 focus:border-slate-400 focus:ring-slate-400/20"
                      data-testid="member-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={memberLoginMutation.isPending}
                  className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-medium shadow-lg hover:shadow-xl transition-all"
                  data-testid="member-login-btn"
                >
                  {memberLoginMutation.isPending ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>Sign In <ArrowRight className="h-4 w-4 ml-2" /></>
                  )}
                </Button>

                <div className="flex justify-between items-center text-sm">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password?type=member')}
                    className="text-slate-500 hover:text-slate-700 hover:underline transition-colors"
                    data-testid="member-forgot-password-link"
                  >
                    Forgot Password?
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/member/register')}
                    className="text-slate-500 hover:text-slate-700 font-medium"
                  >
                    <span className="text-gold">Register</span>
                  </button>
                </div>
              </form>
            </TabsContent>

            {/* Delegate Login */}
            <TabsContent value="delegate">
              <form onSubmit={handleDelegateLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="delegate-email" className="text-slate-700 font-medium">Email or Phone</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="delegate-email"
                      type="text"
                      placeholder="you@example.com or +447700900123"
                      value={delegateForm.email}
                      onChange={(e) => setDelegateForm({ ...delegateForm, email: e.target.value })}
                      className="h-12 pl-12 rounded-xl border-slate-200 focus:border-slate-400 focus:ring-slate-400/20"
                      data-testid="delegate-email-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500">Use your email address or phone number (including country code).</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="delegate-password" className="text-slate-700 font-medium">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="delegate-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={delegateForm.password}
                      onChange={(e) => setDelegateForm({ ...delegateForm, password: e.target.value })}
                      className="h-12 pl-12 pr-12 rounded-xl border-slate-200 focus:border-slate-400 focus:ring-slate-400/20"
                      data-testid="delegate-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={delegateLoginMutation.isPending}
                  className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-medium shadow-lg hover:shadow-xl transition-all"
                  data-testid="delegate-login-btn"
                >
                  {delegateLoginMutation.isPending ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>Sign In <ArrowRight className="h-4 w-4 ml-2" /></>
                  )}
                </Button>

                <div className="flex justify-between items-center text-sm">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password?type=delegate')}
                    className="text-slate-500 hover:text-slate-700 hover:underline transition-colors"
                    data-testid="delegate-forgot-password-link"
                  >
                    Forgot Password?
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/events')}
                    className="text-slate-500 hover:text-slate-700 font-medium"
                  >
                    Register for event <ChevronRight className="h-4 w-4 inline" />
                  </button>
                </div>
              </form>
            </TabsContent>
          </Tabs>

          {/* Browse Without Login - Only show if at least one link is enabled */}
          {(config?.showBrowseEvents !== false || 
            config?.showBrowseChurches !== false || 
            config?.showBrowseCampaigns !== false || 
            config?.showBrowseGiving !== false || 
            config?.showBrowseBible !== false || 
            config?.showBrowseHome !== false) && (
            <div className="mt-8 p-4 bg-slate-50 rounded-xl">
              <p className="text-sm font-medium text-slate-700 mb-3 text-center">Browse without signing in</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {config?.showBrowseEvents !== false && (
                  <button
                    onClick={() => navigate('/events')}
                    className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-white hover:shadow-sm transition-all text-slate-600 hover:text-slate-900"
                    data-testid="browse-events-btn"
                  >
                    <Calendar className="h-5 w-5" />
                    <span className="text-xs font-medium">Events</span>
                  </button>
                )}
                {config?.showBrowseChurches !== false && (
                  <button
                    onClick={() => navigate('/churches')}
                    className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-white hover:shadow-sm transition-all text-slate-600 hover:text-slate-900"
                    data-testid="browse-churches-btn"
                  >
                    <Church className="h-5 w-5" />
                    <span className="text-xs font-medium">Churches</span>
                  </button>
                )}
                {config?.showBrowseCampaigns !== false && (
                  <button
                    onClick={() => navigate('/campaigns')}
                    className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-white hover:shadow-sm transition-all text-slate-600 hover:text-slate-900"
                    data-testid="browse-campaigns-btn"
                  >
                    <Heart className="h-5 w-5" />
                    <span className="text-xs font-medium">Campaigns</span>
                  </button>
                )}
                {config?.showBrowseGiving !== false && (
                  <button
                    onClick={() => navigate('/giving')}
                    className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-white hover:shadow-sm transition-all text-slate-600 hover:text-slate-900"
                    data-testid="browse-giving-btn"
                  >
                    <CreditCard className="h-5 w-5" />
                    <span className="text-xs font-medium">Giving</span>
                  </button>
                )}
                {config?.showBrowseBible !== false && (
                  <button
                    onClick={() => navigate('/bible-home')}
                    className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-white hover:shadow-sm transition-all text-slate-600 hover:text-slate-900"
                    data-testid="browse-bible-btn"
                  >
                    <BookOpen className="h-5 w-5" />
                    <span className="text-xs font-medium">Bible</span>
                  </button>
                )}
                {config?.showBrowseHome !== false && (
                  <button
                    onClick={() => navigate('/')}
                    className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-white hover:shadow-sm transition-all text-slate-600 hover:text-slate-900"
                    data-testid="browse-home-btn"
                  >
                    <Home className="h-5 w-5" />
                    <span className="text-xs font-medium">Home</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-slate-100 text-center">
            <p className="text-xs text-slate-400">
              By signing in, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedLoginPage;
